import React, { useContext } from 'react';
import * as Sentry from '@sentry/react';
import gql from 'graphql-tag';
import { IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import {
  createAuthenticatedApolloClient,
  defaultDataIdFromObject,
  reportingCacheUpdate,
} from './apollo-helpers';
import { resetIdentity, shareAttribution, track } from './analytics';
import pluralize from 'pluralize';
import {
  isArray,
  isEmpty,
  isNumber,
  isPlainObject,
  isString,
  get,
  set,
  omit,
  values,
  keys,
  some,
} from 'lodash';
import { addMilliseconds, subMilliseconds } from 'date-fns';
import { loadFacebookPixel } from './facebook-helpers';
import { sendCJEOrderEvent } from './cj-affiliates-helpers';
import { sendBingEvent } from './bing-ads-helpers';
import { sendOpenAiOrderEvent } from './openai-helpers';

export function sleep(time) {
  return new Promise((resolve) => setTimeout(resolve, time));
}

/**
 * Convert a 6-digit hex color (e.g. "#F47037") into the Tailwind HSL
 * component string (e.g. "16.5 89% 58%") used by `--primary` / `--secondary`.
 */
export function hexToHslString(hex) {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!match) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  const int = parseInt(match[1], 16);
  const r = ((int >> 16) & 0xff) / 255;
  const g = ((int >> 8) & 0xff) / 255;
  const b = (int & 0xff) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      default:
        h = (r - g) / d + 4;
    }
    h *= 60;
  }

  const round = (n) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

/**
 * Reports a completed purchase to the analytics providers.
 *
 * `lines` describes everything actually charged -- the main course, any cross-sell
 * courses, and the one-time addons chosen under each -- so bundled orders report
 * their true item list and total. Callers must exclude cross-sells that failed to
 * charge; those are not revenue. When `lines` is omitted this falls back to
 * reporting the main product alone.
 */
export function sendPaymentStat({ user, product, subscription, lines }) {
  if (!subscription || !user || !product) {
    return;
  }

  const items = isEmpty(lines)
    ? [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: 'Subscription',
          price: (product.price || 0) / 100,
          quantity: 1,
        },
      ]
    : lines.flatMap((line, index) => [
        {
          item_id: line.product.id,
          item_name: line.product.name,
          // Separating the cross-sell category is what makes bundle revenue
          // answerable in GA without a bespoke report. A line can name its own
          // category for the case where a cross-sell is bought on its own after
          // failing in the bundle -- it's still cross-sell revenue, even though
          // it's the only line in that follow-up purchase.
          item_category:
            line.category || (index === 0 ? 'Subscription' : 'Cross-sell'),
          price: (line.product.price || 0) / 100,
          quantity: 1,
        },
        ...(line.addons || []).map((addon) => ({
          item_id: addon.id,
          item_name: addon.name,
          item_category: 'Add-on',
          item_list_name: line.product.name,
          price: (addon.price || 0) / 100,
          quantity: 1,
        })),
      ]);

  const value = items.reduce((sum, item) => sum + item.price, 0);
  const crossSellCount = isEmpty(lines) ? 0 : Math.max(lines.length - 1, 0);

  gtag('event', 'purchase', {
    currency: 'USD',
    transaction_id: subscription.billing,
    value,
    // Flat dimensions so bundle rate and bundled revenue are queryable directly.
    bundled_products: crossSellCount,
    is_bundle: crossSellCount > 0,
    items,
  });

  // Same purchase in PostHog, plus the share that earned it when there is one.
  track('purchase', {
    currency: 'USD',
    transaction_id: subscription.billing,
    value,
    bundled_products: crossSellCount,
    ...shareAttribution(),
  });

  // Send conversion tracking stats to facebook
  const fbPixelId =
    window.ReactAppEnv &&
    window.ReactAppEnv.stats &&
    window.ReactAppEnv.stats.fbPixelId;
  if (fbPixelId) {
    const fbq = loadFacebookPixel({ pixelId: fbPixelId });
    fbq('track', 'Purchase', {
      content_category: 'Subscription',
      content_ids: items.map((item) => item.item_id),
      content_name: product.name,
      content_type: 'product',
      currency: 'USD',
      value,
    });
  }

  // Send conversion tracking stats to cj affiliates
  // for the first subscription a user purchases.
  sendCJEOrderEvent({
    orderId: subscription.billing,
    currency: 'USD',
    amount: value,
    customerStatus:
      user.subscriptions && user.subscriptions.length > 1 ? 'return' : 'new',
  });

  // Send conversion tracking stats to bing
  sendBingEvent(product.id, product.name, value);

  // Send conversion tracking stats to openai ads
  sendOpenAiOrderEvent({
    orderId: subscription.billing,
    value,
    currency: 'USD',
    items,
  });
}

/**
 * Lightweight GA4 event tracking for the registration flows.
 * Callers pass `flow: 'old' | 'new'` and `product` (slug) in params.
 * Must never throw or block the flow it instruments.
 */
export function trackRegistrationEvent(name, params = {}) {
  try {
    if (typeof window.gtag !== 'function') {
      return;
    }
    const safeParams =
      params.error == null
        ? params
        : { ...params, error: String(params.error).slice(0, 200) };
    window.gtag('event', name, safeParams);
  } catch (_err) {
    // Tracking failures must never break registration.
  }
}

// Expected user-mistake Cognito errors (`code` field) — not worth a Sentry
// event. InvalidPasswordException is deliberately NOT listed (#378).
const EXPECTED_REGISTRATION_ERROR_CODES = [
  'NotAuthorizedException',
  'UsernameExistsException',
  'UserNotFoundException',
  'UserNotConfirmedException',
  'CodeMismatchException',
  'ExpiredCodeException',
];

export function reportRegistrationError(step, flow, error, extra = {}) {
  try {
    if (EXPECTED_REGISTRATION_ERROR_CODES.includes(error?.code)) {
      return;
    }
    Sentry.captureException(error, {
      tags: { registration_step: step, flow },
      extra,
    });
  } catch (_err) {
    // Reporting failures must never break registration.
  }
}

export async function authSignUp(authParams) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  const userAttributes = keys(authParams.attributes).map((key) => ({
    Name: key,
    Value: authParams.attributes[key],
  }));

  return new Promise((resolve, reject) => {
    _state.userPool.signUp(
      authParams.username,
      authParams.password,
      userAttributes,
      null,
      (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      },
    );
  });
}

export async function authSignIn(username, password) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  const user = new CognitoUser({
    Username: username,
    Pool: _state.userPool,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(authenticationDetails, {
      onSuccess: (result) => resolve(result),
      onFailure: (err) => reject(err),
    });
  });
}

export function authSignOut() {
  const user = _state.userPool && _state.userPool.getCurrentUser();
  if (user) {
    user.signOut();
  }
}

export async function authRequestPasswordReset(username) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  if (!username) {
    throw new Error('Empty username');
  }

  const user = new CognitoUser({
    Username: username,
    Pool: _state.userPool,
  });

  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
      inputVerificationCode: (data) => resolve(data),
    });
  });
}

export async function authUpdateAttributes(username, attributes) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  if (isEmpty(username)) {
    throw new Error('InvalidParam: username');
  }

  if (isEmpty(attributes)) {
    throw new Error('InvalidParam: attributes');
  }

  if (!_state.authUser) {
    throw new Error('User is not authenticated');
  }

  const user = await getAuthUser();
  const isUpdatingSelf = await new Promise((resolve, reject) => {
    if (username === user.username) {
      resolve(true);
      return;
    }

    user.getUserAttributes((err, attributes) => {
      if (err) {
        reject(err);
      } else {
        resolve(some(attributes, { Name: 'email', Value: username }));
      }
    });
  });

  const userAttributes = keys(attributes).map((key) => ({
    Name: key,
    Value: attributes[key],
  }));

  return new Promise((resolve, reject) => {
    if (isUpdatingSelf) {
      user.updateAttributes(userAttributes, (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      });
    } else {
      throw new Error('Admin auth attributes is not implemented');
    }
  });
}

export async function authConfirmPasswordReset(username, code, newPassword) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  if (!username) {
    throw new Error('Empty username');
  }

  if (!code) {
    throw new Error('Empty code');
  }

  if (!newPassword) {
    throw new Error('Empty password');
  }

  const user = new CognitoUser({
    Username: username,
    Pool: _state.userPool,
  });

  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: resolve,
      onFailure: reject,
    });
  });
}

export async function authConfirmRegistration(username, code) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  if (!username) {
    throw new Error('Empty username');
  }

  if (!code) {
    throw new Error('Empty code');
  }

  const user = new CognitoUser({
    Username: username,
    Pool: _state.userPool,
  });

  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, false, (err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
}

export async function authResendConfirmationCode(username) {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  if (!username) {
    throw new Error('Empty username');
  }

  const user = new CognitoUser({
    Username: username,
    Pool: _state.userPool,
  });

  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err, result) => {
      if (err) {
        reject(err);
      } else {
        resolve(result);
      }
    });
  });
}

let _tickedObjects = [];

export function registerTickedObject(obj, interval, callback) {
  unregisterTickedObject(obj);

  const entry = { timer: null, callback, obj };
  _tickedObjects.push(entry);

  const tick = async () => {
    await (entry.callback || obj.tick || (() => {}))();

    if (isActiveTickedObject(obj)) {
      entry.timer = setTimeout(tick, interval);
    }
  };

  tick();
}

export function unregisterTickedObject(obj, finalTick) {
  const entry = _tickedObjects.find((entry) => entry.obj === obj);
  if (entry) {
    _tickedObjects = _tickedObjects.filter((entry) => entry.obj !== obj);

    clearTimeout(entry.timer);
    if (finalTick) {
      (entry.callback || obj.tick || (() => {}))();
    }
  }
}

export function isActiveTickedObject(obj) {
  return _tickedObjects.some((entry) => entry.obj === obj);
}

export function clearAllTickedObjects() {
  _tickedObjects.forEach((value) => {
    clearTimeout(value.timer);
  });
  _tickedObjects = [];
}

const _state = {
  apiClient: null,
  authState: 'loading',
  authUser: null,
  appConfig: {},
  userPool: null,
};

export const AppContext = React.createContext({});
export function useApp() {
  return useContext(AppContext);
}

export function withAppContext(WrappedComponent, propName) {
  return (props) => (
    <AppContext.Consumer>
      {(context) => (
        <WrappedComponent {...props} {...{ [propName || 'app']: context }} />
      )}
    </AppContext.Consumer>
  );
}

export function getApiClient() {
  if (!_state.apiClient) {
    throw new Error('Api client is not ready.');
  }

  return _state.apiClient;
}

export function isAuthenticated() {
  return _state.authState === 'signedIn';
}

export function isUserInGroup(group, user) {
  const groups = user.groups;
  return groups && groups.includes(group);
}

export function getAuthState() {
  return _state.authState;
}

export function getAppConfig() {
  return _state.appConfig;
}

export function getAppServerUri() {
  return (
    ((window.ReactAppEnv || {}).api || {}).uri ||
    import.meta.env.VITE_API_SERVER_URI
  );
}

function getAuthUser() {
  if (!_state.userPool) {
    throw new Error('Userpool is not initialized.');
  }

  const user = _state.userPool.getCurrentUser();
  if (!user) {
    throw new Error('No current user');
  }

  return new Promise((resolve, reject) => {
    user.getSession((err) => {
      if (err) {
        reject(err);
      } else {
        resolve(user);
      }
    });
  });
}

async function getAuthSession() {
  if (!_state.authUser) {
    return null;
  }

  return new Promise((resolve, reject) => {
    _state.authUser.getSession((err, session) => {
      if (err) {
        reject(err);
      } else {
        resolve(session);
      }
    });
  });
}

/**
 * The Cognito ID token sgapp's GraphQL authorizes against. Exported because
 * the activity API needs it forwarded too: it reads classmates' enrollments
 * from sgapp as the learner, and `user.token` -- signed with sgapp's
 * TOKEN_SECRET -- is a credential its GraphQL accepts nowhere.
 */
export async function getAuthToken() {
  if (!_state.authUser) {
    return null;
  }

  const session = await getAuthSession();
  const token = session.idToken.jwtToken;
  return token;
}

async function initializeApiClient() {
  _state.apiClient = await createAuthenticatedApolloClient({
    uri: getAppServerUri(),
    authTokenCallback: getAuthToken,
    cache: {
      freezeResults: true,
      // Apollo's heuristic matcher cannot tell union members apart, so it
      // drops interactive activity content once the union has two types.
      fragmentMatcher: new IntrospectionFragmentMatcher({
        introspectionQueryResultData: {
          __schema: {
            types: [
              {
                kind: 'UNION',
                name: 'InteractiveActivityContent',
                possibleTypes: [
                  { name: 'BranchingScenario' },
                  { name: 'ShootOrDontShootGame' },
                ],
              },
            ],
          },
        },
      }),
      dataIdFromObject: (obj) =>
        obj.__typename && obj.guid
          ? `${obj.__typename}:${obj.guid}`
          : defaultDataIdFromObject(obj),
    },
  });
}

export async function initialize() {
  clearAllTickedObjects();
  Object.assign(_state, {
    userPool: new CognitoUserPool(window.ReactAppEnv.auth),
    apiClient: null,
    authState: 'loading',
    authUser: null,
    appConfig: {},
  });

  await initializeApiClient();
}

/**
 * Fetches the client app config into the state `getAppConfig()` reads. Kept
 * apart from initialize() so a caller can send it alongside login(), which
 * needs only the api client that initialize() has already made.
 */
export async function loadClientAppConfig() {
  _state.appConfig = await getClientAppConfig();
}

export async function logout() {
  resetIdentity();
  clearAllTickedObjects();
  Object.assign(_state, {
    authState: 'signIn',
    authUser: null,
  });

  authSignOut();
}

export async function login() {
  Object.assign(_state, {
    authState: 'loading',
    authUser: null,
  });

  try {
    if (!_state.userPool) {
      throw new Error('Userpool is not initialized.');
    }

    const user = _state.userPool.getCurrentUser();
    if (!user) {
      _state.authState = 'signIn';
      return;
    }

    await new Promise((resolve, reject) => {
      user.getSession((err) => {
        if (err) {
          reject(err);
        } else {
          resolve(user);
        }
      });
    });

    _state.authUser = await getAuthUser();
    _state.authState = 'signedIn';
  } catch (e) {
    console.error(e);
    console.error('User is not authenticated.');
    _state.authState = 'signIn';
  }

  const session = await getAuthSession();
  if (!session) {
    return;
  }

  return await api.users.login();
}

export async function getClientAppConfig() {
  const client = getApiClient();
  const query = gql`
    query GetClientAppConfig {
      clientAppConfig
    }
  `;

  const result = await client.query({ query });
  const data = JSON.parse(result.data.clientAppConfig);
  const clientTime = new Date();
  const serverTime = data.serverTime ? new Date(data.serverTime) : clientTime;
  const timeOffset = clientTime - serverTime;

  return {
    ...omit(data, 'serverTime'),
    serverToClientTime: (t) => addMilliseconds(t, timeOffset),
    clientToServerTime: (t) => subMilliseconds(t, timeOffset),
  };
}

export function prepareSearchQueryString(str, defaultKey) {
  const filters = (str || '')
    .split(';')
    .filter((filter) => !isEmpty(filter))
    .map((filter) => {
      const parts = filter.split(/=(.*)/, 2);
      const key = parts.length > 1 ? parts[0].trim() : defaultKey;
      const valueIndex = parts.length > 1 ? 1 : 0;
      const value = escapeSearchQueryString(parts[valueIndex].trim());
      return key ? `${key}=${value}` : value;
    })
    .join(';');
  return filters;
}

export function escapeSearchQueryString(str) {
  str = str.trim();

  const isPattern = str.match(/^@{.*}$/);
  if (isPattern) {
    return str.substring(2, str.length - 1);
  }

  const matchBeginning = str.startsWith('^');
  const matchEnd = str.endsWith('$');
  str = str.slice(matchBeginning ? 1 : 0, matchEnd ? -1 : str.length);
  str = str.replace(/([().%+\-*?[^$])/g, '%$&');
  return `${matchBeginning ? '^' : ''}${str}${matchEnd ? '$' : ''}`;
}

export function calculateEnrollmentId(userId, courseId) {
  return `${userId}_${courseId}`;
}

export function calculateMembershipId(orgId, userId) {
  return `${orgId}_${userId}`;
}

export function calculateStripePlanId(product) {
  const isAppManaged =
    isEmpty(product.billing) ||
    product.billing === 'auto' ||
    product.billing === calculateStripeProductId(product);

  return isAppManaged ? null : product.billing;
}

export function calculateStripeProductId(product) {
  return product.id && `prod_${product.id}`;
}

export function calculateStripePlanUrl(product, live) {
  const planId = calculateStripePlanId(product);
  return planId
    ? `https://dashboard.stripe.com/${live ? '' : 'test/'}plans/${calculateStripePlanId(product)}`
    : `https://dashboard.stripe.com/${live ? '' : 'test/'}products/${calculateStripeProductId(product)}`;
}

export function priceStringToPrice(price) {
  price = price.trim().replace(/\$/, '').trim();
  return Math.floor(parseFloat(price) * 100);
}

export function priceToPriceString(price) {
  return `$${(price / 100).toFixed(2)}`;
}

export function formatProductPrice(product) {
  let price = priceToPriceString(product.price);
  if (product.autocancel) {
    return price;
  }

  if (product.intervalCount === 1) {
    return `${price} per ${product.interval}`;
  }

  return `${price} billed every ${pluralize(product.interval, product.intervalCount, product.intervalCount > 1)}`;
}

export async function listAllItems(query, params, options) {
  params = { ...params };
  params.first = params.first || 0;
  params.offset = params.offset || 0;
  params.query = params.query && prepareSearchQueryString(params.query);

  options = {
    fetchPolicy: 'no-cache',
    ...options,
  };

  // There seems to be a bug, where no data is return sometimes
  // if cache-first is specified explicitly. So, just allow
  // apollo to default to cache-first if that is what is requested
  if (options.fetchPolicy === 'cache-first') {
    delete options.fetchPolicy;
  }

  const variables = { params: { ...params } };
  const client = getApiClient();
  const result = {
    totalCount: 0,
    items: [],
    cursor: null,
  };

  let moreRequested = true;
  let moreAvailable = true;
  while (moreRequested && moreAvailable) {
    const subResult = await client.query({ query, variables, ...options });
    const data =
      (subResult && subResult.data && values(subResult.data)[0]) || {};
    if (!data.items) {
      console.info(query);
      console.info(params);
      console.info(subResult);
    }
    result.items.push(...(data.items || []));
    result.totalCount = data.totalCount;
    result.cursor = data.cursor;

    variables.params.first = Math.max(0, params.first - result.items.length);
    variables.params.after = data.cursor;

    // If we are running a search, then we will be sending a cursor, so we want to reduce the
    // offset by the amount of items the cursor has already skipped. However, if we are listing
    // items, we should add to the offset, so that the listing will pick up where it left off before
    variables.params.offset = params.query
      ? Math.max(0, params.offset - result.items.length)
      : params.offset + result.items.length;

    moreRequested = !params.first || result.items.length < params.first;
    moreAvailable =
      !!variables.params.after ||
      data.totalCount > result.items.length + params.offset;
  }

  return result;
}

export async function query(query, variables, options) {
  const client = getApiClient();
  const result = await client.query({ query, variables, ...options });
  return result;
}

export async function mutate(mutation, variables, options) {
  const client = getApiClient();
  const result = await client.mutate({ mutation, variables, ...options });
  return result;
}

export function extractReferencedFragmentNames(def, results, doc) {
  results = results || [];

  if (def && def.selectionSet && def.selectionSet.selections) {
    def.selectionSet.selections.forEach((child) => {
      if (
        child.kind === 'FragmentSpread' &&
        !results.includes(child.name.value)
      ) {
        results.push(child.name.value);
      }

      extractReferencedFragmentNames(child, results, doc);
    });
  }

  if (def && def.kind === 'FragmentSpread') {
    const fragmentDef = doc.definitions.find((d) => {
      return d.kind === 'FragmentDefinition' && d.name.value === def.name.value;
    });

    if (fragmentDef) {
      extractReferencedFragmentNames(fragmentDef, results, doc);
    }
  }

  return results;
}

export function extractQuery(doc, name) {
  const query = doc.definitions.find(
    (def) => def.kind === 'OperationDefinition' && def.name.value === name,
  );
  if (!query) {
    console.error('Query %s could not be found.', name);
    return null;
  }

  const queryDoc = {
    kind: 'Document',
    definitions: [query],
  };

  const referencedFragments = extractReferencedFragmentNames(query, null, doc);
  referencedFragments.forEach((name) => {
    const fragmentDef = doc.definitions.find((def) => {
      return def.kind === 'FragmentDefinition' && def.name.value === name;
    });

    if (fragmentDef) {
      queryDoc.definitions.push(fragmentDef);
    }
  });

  return queryDoc;
}

export function scrubLocalValues(param) {
  if (isArray(param)) {
    return param.map((child) => scrubLocalValues(child));
  }

  if (isPlainObject(param)) {
    return Object.keys(param).reduce((processed, key) => {
      return key.startsWith('_')
        ? processed
        : { ...processed, [key]: scrubLocalValues(param[key]) };
    }, 0);
  }

  return param;
}

export function transformParams(params, transforms) {
  if (!isArray(transforms)) {
    transforms = [transforms];
  }

  transforms.forEach((transform) => {
    if (transform) {
      params = transform(params);
    }
  });

  params = scrubLocalValues(params);

  return params;
}

export function buildQueryMethod(
  doc,
  name,
  paramTransforms,
  prepareForViewing,
  options,
) {
  const queryDoc = extractQuery(doc, name);
  return async (params, queryOptions) => {
    try {
      params = transformParams(params, paramTransforms);
      const result = await query(queryDoc, params, {
        ...options,
        ...queryOptions,
      });
      const entity = values(result.data)[0];
      return prepareForViewing ? prepareForViewing(entity) : entity;
    } catch (e) {
      console.error('Failed to run query %s', name);
      throw e;
    }
  };
}

export function buildMutateMethod(
  doc,
  name,
  paramTransforms,
  prepareForViewing,
  options,
) {
  const mutation = extractQuery(doc, name);
  return async (params, mutateOptions) => {
    try {
      params = transformParams(params, paramTransforms);
      const result = await mutate(mutation, params, {
        ...options,
        ...mutateOptions,
      });
      const entity = values(result.data)[0];
      return prepareForViewing ? prepareForViewing(entity) : entity;
    } catch (e) {
      console.error('Failed to run mutation %s', name);
      throw e;
    }
  };
}

export function buildListQueryMethod(doc, name) {
  const queryDoc = extractQuery(doc, name);
  return (params, options) => listAllItems(queryDoc, params, options);
}

export const VideoContentFields = `
  id
  name
  poster {
    asset {
      url
    }
  }
  sources {
    asset {
      url
      mimetype
    }
  }
  tracks {
    asset {
      url
    }
    label
    kind
    srclang
  }
`;

export const RichContentFields = `
  text
  images {
    id
    name
    url
    altText
  }
  videos {
    ${VideoContentFields}
  }
  sounds {
    id
    name
    sources {
      asset {
        url
      }
    }
  }
`;

export const RubricFields = `
  id
  name
  criteria {
    key
    description
    weight
    group
  }
  ratings {
    label
    score
  }
  scoreMethod
  scoreRanges {
    min
    max
    score
  }
`;

const queries = gql`
  fragment ContentGroupFields on ContentGroup {
    id
    name
    courses {
      id
      name
      displayName
    }
  }

  query ContentGroupList($params: ListInput!) {
    contentGroups(params: $params) {
      totalCount
      items {
        ...ContentGroupFields
      }
    }
  }

  fragment RichContentFields on RichContent {
    ${RichContentFields}
  }

  fragment RubricFields on Rubric {
    ${RubricFields}
  }

  fragment CourseDescriptionFields on Course {
    id
    name
    displayName
    upgrade {
      id
      displayName
      migrateProgress
    }
    isPreviewing
    concepts {
      id
      topics {
        id
      }
      materials {
        id
      }
      cards {
        id
      }
      activities {
        id
        type
        name
        displayName
      }
    }
    sections {
      id
      name
      concepts {
        id
        name
        topics {
          id
          name
          displayName
          materials {
            id
          }
        }
        cards {
          id
        }
        activities {
          id
          type
          name
          displayName
        }
      }
    }
    previewConcepts {
      id
    }
    practiceTestPassingScore
    completionMessage {
      title
      body
      noteTitle
      note
      noteTone
      links {
        label
        href
      }
    }
    translationLanguages
  }

  fragment ProgressInfoFields on ProgressInfo {
    viewed
    total
  }

  fragment AssessmentSummaryFields on Assessment {
    id
    cid
    guid
    type
    time
    status
    startedAt
    expiresAt
    updatedAt
    completedAt
    sections {
      id
      name
      displayName
      type
      time
      weight
      startedAt
      expiresAt
      updatedAt
      completedAt
    }
  }

  fragment AssessmentQuestionFields on AssessmentQuestion {
    id
    guid
    response
    score
    content {
      id
      responseType
      multiResponse
      reading {
        id
        content { ...RichContentFields }
      }
      readingGroup {
        id
        name
        displayName
        readings {
          displayName
          reading {
            id
            content { ...RichContentFields }
          }
        }
      }
      prompt {
        ...RichContentFields
      }
      answers {
        content {
          ...RichContentFields
        }
        explanation {
          ...RichContentFields
        }
        correct
      }
      explanation {
        ...RichContentFields
      }
      rubric {
        ...RubricFields
      }
    }
    feedback {
      notes {
        id
        content
        source
        deleted
        createdAt
        updatedAt
        updatedBy {
          id
          firstName
          lastName
        }
      }
      rubric {
        ratings {
          criterionKey
          ratingIndex
        }
        source
      }
      score
      source
    }
  }

  fragment AssessmentFields on Assessment {
    ...AssessmentSummaryFields
    questions { ...AssessmentQuestionFields }
    sections {
      questions { ...AssessmentQuestionFields }
    }
  }

  fragment AssessmentStatsFields on AssessmentStats {
    id
    cid
    guid
    time
    type
    questions { ...ProgressInfoFields }
    sections {
      id
      name
      displayName
      time
      weight
      questions { ...ProgressInfoFields }
      crqScoreSources
      score {
        raw
        max
        weight
        normalized
      }
    }
    score {
      raw
      max
      normalized
    }
    mcqScore {
      normalized
      raw
      max
    }
    crqScore {
      raw
      max
      normalized
    }
    crqScoreSources
    status
    startedAt
    expiresAt
    completedAt
  }

  fragment ConceptStatsFields on ConceptStats {
    id
    guid
    materials { ...ProgressInfoFields }
    cards { ...ProgressInfoFields }
    questions { ...ProgressInfoFields }
    quizzes { ...ProgressInfoFields }
    score {
      raw
      max
    }
    progress
    hide
  }

  fragment EnrollmentStatsFields on EnrollmentStats {
    questions { ...ProgressInfoFields }
    materials { ...ProgressInfoFields }
    cards { ...ProgressInfoFields }
    quizzes: assessments(type: "quiz") {
      id
      guid
      status
      passed
      startedAt
      score {
        normalized
      }
      questions {
        total
        viewed
      }
    }
    concepts { ...ConceptStatsFields }
    assessments { ...AssessmentStatsFields }
    practiceTests: assessments(type: "practice") { ...AssessmentStatsFields }
    diagnosticTests: assessments(type: "diagnostic") { ...AssessmentStatsFields }
    progress
  }

  fragment EnrollmentSummaryFields on Enrollment {
    id
    createdAt
    updatedAt
    course {
      ...CourseDescriptionFields
      assessments {
        cid
        type
        time
        questions
      }
    }
    stats { ...EnrollmentStatsFields }
    activity { ...EnrollmentActivityFields }
    user { id }
    access
    product { ...ProductFields }
  }

  fragment EnrollmentStatsSummaryUpdateFields on Enrollment {
    id
    updatedAt
    stats {
      questions { ...ProgressInfoFields }
      materials { ...ProgressInfoFields }
      cards { ...ProgressInfoFields }
      progress
      ptScore
    }
    access
  }

  fragment EnrollmentActivityFields on EnrollmentActivity {
    quizzes
    materials
    cards
  }

  mutation EnrollmentViewDashboard($id: ID!) {
    enrollmentAccess(id: $id) {
      ...EnrollmentSummaryFields
      studyPlan {
        unlocked
      }
      course {
        id
        sections {
          id
          name
          concepts {
            id
            name
            examNodes {
              index
            }
          }
        }
        examNodes {
          index
          parent
          key
          name
          description
        }
        upgrade {
          id
          displayName
          migrateProgress
        }
        introVideo {
          ${VideoContentFields}
        }
        previewConcepts {
          id
        }
      }
    }
  }

  mutation EnrollmentClaim($id: ID!) {
    enrollmentClaim(id: $id) {
      ...EnrollmentSummaryFields
    }
  }

  mutation EnrollmentAddPracticeTestAttempt($id: ID!) {
    enrollmentAddPracticeTestAttempt(id: $id) {
      ...EnrollmentSummaryFields
    }
  }

  query EnrollmentExport($id: ID!, $compressed: Boolean) {
    enrollmentExport(id: $id, compressed: $compressed)
  }

  mutation EnrollmentImport($id: ID!, $data: String!, $allowCourseMismatch: Boolean) {
    enrollmentImport(id: $id, data: $data, allowCourseMismatch: $allowCourseMismatch) {
      ...EnrollmentSummaryFields
      stats {
        quizzes: assessments(type: "quiz") { ...AssessmentStatsFields }
      }
    }
  }

  query EnrollmentGet($id: ID!, $context: ClientContext) {
    enrollment(id: $id, context: $context) {
      ...EnrollmentSummaryFields
    }
  }

  query EnrollmentList($params: ListInput!) {
    enrollments(params: $params) {
      totalCount
      cursor
      items {
        ...EnrollmentSummaryFields
      }
    }
  }

  mutation EnrollmentCreate($user: ID!, $course: ID!, $product: ID) {
    enrollmentCreate(user: $user, course: $course, product: $product) {
      ...EnrollmentSummaryFields
      stats {
        quizzes: assessments(type: "quiz") { ...AssessmentStatsFields }
      }
      user {
        enrollments { ...EnrollmentSummaryFields }
      }
    }
  }

  mutation EnrollmentDelete($id: ID!) {
    enrollmentDelete(id: $id) { id }
  }

  mutation EnrollmentReset($id: ID!, $context: ClientContext) {
    enrollmentResetProgress(id: $id, context: $context) {
      ...EnrollmentSummaryFields
      stats {
        quizzes: assessments(type: "quiz") { ...AssessmentStatsFields }
      }
      user {
        enrollments { ...EnrollmentSummaryFields }
      }
    }
  }

  mutation EnrollmentRecertify($id: ID!) {
    enrollmentRecertify(id: $id) {
      id
      updatedAt
      completionCertificate {
        id
        createdAt
        expiresAt
        isExpired
      }
      stats {
        progress
        ptScore
      }
    }
  }

  mutation EnrollmentViewContent($id: ID!, $content: [ID], $source: String) {
    viewContent(enrollment: $id, content: $content, source: $source) {
      logs {
        id
        entries {
          id
        }
      }
      stats { ...ConceptStatsFields }
      enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EnrollmentLogActivity($id: ID!, $activity: String!, $time: Int!) {
    enrollmentLogActivity(id: $id, activity: $activity, time: $time) {
      id
      activity {
        quizzes
        materials
        cards
        total
      }
      updatedAt
      accessedAt
    }
  }

  mutation EnrollmentStartAssessment($id: ID!, $assessment: ID!, $reset: Boolean, $time: DateTime) {
    assessmentStart(enrollment: $id, assessment: $assessment, reset: $reset, time: $time) {
      ...AssessmentFields
    }
  }

  mutation EnrollmentResetAssessment($id: ID!, $assessment: ID!) {
    assessmentReset(enrollment: $id, assessment: $assessment) {
      ...AssessmentFields
    }
  }

  mutation EnrollmentSubmitAssessment($id: ID!, $assessment: ID!, $responses: [String]) {
    assessmentComplete(enrollment: $id, assessment: $assessment, responses: $responses) {
      assessment {
        ...AssessmentFields
      }
      astat { ...AssessmentStatsFields }
      cstat { ...ConceptStatsFields }
      enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EndrollmentAssessmentSubmitFeedback($enrollment: ID!, $assessment: ID!, $question: ID!, $feedback: JSON!){
    assessmentSubmitFeedback(enrollment: $enrollment, assessment: $assessment, question: $question, feedback: $feedback) {
        assessment { ...AssessmentFields }
        question { ...AssessmentQuestionFields }
        section { ...AssessmentFields }
        enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EnrollmentAssessmentSubmitFeedbackSource($enrollment: ID!, $assessment: ID!, $question: ID!, $source: String!){
    assessmentSubmitFeedbackSource(enrollment: $enrollment, assessment: $assessment, question: $question, source: $source) {
        assessment { ...AssessmentFields }
        question { ...AssessmentQuestionFields }
        section { ...AssessmentFields }
        enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation EnrollmentSubmitAssessmentResponse($id: ID!, $assessment: ID!, $question: ID!, $response: String!, $index: Int) {
    assessmentSubmitResponse(enrollment: $id, assessment: $assessment, question: $question, response: $response, index: $index) {
      question { ...AssessmentQuestionFields }
      astat { ...AssessmentStatsFields }
      cstat { ...ConceptStatsFields }
      enrollment { ...EnrollmentStatsSummaryUpdateFields }
    }
  }

  mutation GradingRequestCreate($user: ID!, $enrollment: ID!, $assessment:  ID!, $question: ID!, $response: String!) {
    gradingRequestCreate(user: $user, enrollment: $enrollment, assessment: $assessment, question: $question, response: $response) {
      id
    }
  }

  fragment UserSummaryFields on User {
    id
    firstName
    lastName
    email
    preferredLanguage
    createdAt
    updatedAt
    loggedInAt
    groups
    live
    credits {
      grading
    }
    disableActivityTimers
    job {
      city
      state
      description
      commuteDistance
      subjects
      grades
      isCertified
      isInCertificationProgram
      hasPassedCertificationExam
      isCertificationOther
      certificationStatusOther
      testsStudying
      testsPassed
      gradeLevels
      roles
      states
      cities
      certificationStatusCurrent
      certificationStates
      certificationTestsPassed
      workingStart
      workingStartOther
      educationHighest
      educationMajorField
      educationSchoolName
      educationGraduationDate
      workExperienceJobTitle
      workExperienceJobEmployer
      workExperienceJobStartDate
      workExperienceJobEndDate
      workExperienceWorkHere
      phoneNumber
      jobSearchLookingForJob
      jobSearchNotLookingWhy
      jobSearchNotLookingWhyOther
      jobSearchGetStarted
      jobSearchCreateProfile
      jobSearchPrimaryState
      agreedToShareInformation
      doNotAskAgain
      searchRecommended
      searchSaved
      searchSavedEntries
      jobUpdatedAt
    }
  }

  fragment BillingInfoFields on BillingInfo {
    method
    account
    expMonth,
    expYear,
  }

  fragment InvoiceFields on Invoice {
    description
    createdAt
    periodStartedAt
    periodExpiredAt
    amount
    status
    url
  }

  fragment SubscriptionFields on Subscription {
    billing
    createdAt
    expiresAt
    removedAt
    status
    product { ...ProductFields }
    key {
      id
      claimedAt
      expiresAt
      duration
    }
  }

  query ListProviderUsers($params: ListInput) {
    providerUsers(params: $params) {
      cursor
      items {
        uuid
        email
        firstName
        lastName
        user { ...UserSummaryFields }
      }
    }
  }

  query UserGet($id: ID!, $context: ClientContext) {
    user(id: $id, context: $context) {
      ...UserSummaryFields
    }
  }

  query UserList($params: ListInput) {
    users(params: $params) {
      totalCount
      cursor
      items {
        ...UserSummaryFields
      }
    }
  }

  query UserGetBilling($id: ID!) {
    user(id: $id) {
      id
      billing { ...BillingInfoFields }
    }
  }

  query UserGetSubscriptions($id: ID!) {
    user(id: $id) {
      id
      subscriptions { ...SubscriptionFields }
    }
  }

  query UserGetInvoices($id: ID!) {
    user(id: $id) {
      id
      invoices { ...InvoiceFields }
    }
  }

  query UserGetEnrollments($id: ID!, $context: ClientContext) {
    user(id: $id, context: $context) {
      id
      enrollments { ...EnrollmentSummaryFields }
    }
  }

  # The header only needs to know whether any enrolled course is translated, so
  # this skips the stats, activity and product that UserGetEnrollments carries.
  query UserGetEnrollmentTranslations($id: ID!, $context: ClientContext) {
    user(id: $id, context: $context) {
      id
      enrollments {
        id
        course {
          id
          translationLanguages
        }
      }
    }
  }

  query UserGetMemberships($id: ID!) {
    user(id: $id) {
      id
      memberships { ...UserMembershipSummaryFields }
    }
  }

  query UserGetJobSearchSaved($id: ID!) {
    jobSearchSaved(id: $id) {
      savedJobs
    }
  }

  query UserGetCourses($id: ID!) {
    user(id: $id) {
      id
      courses { ...CourseDescriptionFields }
    }
  }

  mutation UserLogin {
    login {
      ...UserSummaryFields
      memberships {
        ...UserMembershipSummaryFields
      }
      subscriptions {
        ...SubscriptionFields
      }
      token
    }
  }

  mutation CreatePreviewSubscriptionInvoice($user: ID, $products: [ID], $lines: [CrossSellInput], $payment: String!, $profile: UserProfileInput) {
    previewSubscriptionInvoiceCreate(user: $user, products: $products, lines: $lines, payment: $payment, profile: $profile) {
      subtotal
      tax
      total
      items {
        id
        description
        subtotal
        interval
        intervalCount
      }
    }
  }

  # A separate document rather than an extraSeats arg on the mutation above:
  # GraphQL validates arguments even when their variable is unset, so folding the
  # arg into the shared document would break every checkout against a server that
  # doesn't know group seats yet. This variant is only ever sent when the buyer
  # actually picked seats, which the group-seats flag gates.
  mutation CreatePreviewSubscriptionInvoiceWithSeats($user: ID, $products: [ID], $lines: [CrossSellInput], $payment: String!, $profile: UserProfileInput, $extraSeats: Int) {
    previewSubscriptionInvoiceCreate(user: $user, products: $products, lines: $lines, payment: $payment, profile: $profile, extraSeats: $extraSeats) {
      subtotal
      tax
      total
      items {
        id
        description
        subtotal
        interval
        intervalCount
      }
    }
  }

  mutation UserCancelSubscription($user: ID!, $subscription: ID!, $timing: String) {
    subscriptionCancel(user: $user, subscription: $subscription, timing: $timing) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserRemoveSubscription($user: ID!, $subscription: ID!) {
    subscriptionRemove(user: $user, subscription: $subscription) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserCreateSubscription($user: ID!, $product: ID!, $payment: String, $selectedAddons: [ID], $profile: UserProfileInput, $paymentToken: String, $isRecertification: Boolean) {
    subscriptionCreate(user: $user, product: $product, payment: $payment, selectedAddons: $selectedAddons, profile: $profile, paymentToken: $paymentToken, isRecertification: $isRecertification) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserCreateSubscriptionBundle($user: ID!, $product: ID!, $payment: String, $selectedAddons: [ID], $selectedCrossSells: [CrossSellInput], $profile: UserProfileInput, $paymentToken: String, $isRecertification: Boolean) {
    subscriptionBundleCreate(user: $user, product: $product, payment: $payment, selectedAddons: $selectedAddons, selectedCrossSells: $selectedCrossSells, profile: $profile, paymentToken: $paymentToken, isRecertification: $isRecertification) {
      user {
        ...UserSummaryFields
        subscriptions { ...SubscriptionFields }
      }
      failures {
        message
        product { id name price }
      }
    }
  }

  # Separate for the same reason as CreatePreviewSubscriptionInvoiceWithSeats:
  # only sent when extraSeats > 0, so seatless purchases keep the exact document
  # (and server compatibility) they had before group seats existed.
  mutation UserCreateSubscriptionBundleWithSeats($user: ID!, $product: ID!, $payment: String, $selectedAddons: [ID], $selectedCrossSells: [CrossSellInput], $profile: UserProfileInput, $paymentToken: String, $extraSeats: Int, $isRecertification: Boolean) {
    subscriptionBundleCreate(user: $user, product: $product, payment: $payment, selectedAddons: $selectedAddons, selectedCrossSells: $selectedCrossSells, profile: $profile, paymentToken: $paymentToken, extraSeats: $extraSeats, isRecertification: $isRecertification) {
      user {
        ...UserSummaryFields
        subscriptions { ...SubscriptionFields }
      }
      failures {
        message
        product { id name price }
      }
    }
  }

  mutation UserGroupSeatsPurchase($user: ID!, $product: ID!, $extraSeats: Int!, $ref: String!, $payment: String) {
    groupSeatsPurchase(user: $user, product: $product, extraSeats: $extraSeats, ref: $ref, payment: $payment) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserAddonPurchase($user: ID!, $product: ID!, $addon: ID!, $quantity: Int!, $ref: String!, $payment: String) {
    addonPurchase(user: $user, product: $product, addon: $addon, quantity: $quantity, ref: $ref, payment: $payment) {
      ...UserSummaryFields
      subscriptions { ...SubscriptionFields }
    }
  }

  mutation UserAddonPurchasePreviewInvoiceCreate($user: ID!, $product: ID!, $addon: ID!, $quantity: Int!) {
    addonPurchasePreviewInvoiceCreate(user: $user, product: $product, addon: $addon, quantity: $quantity) {
      subtotal
      tax
      total
      items { id description quantity subtotal }
    }
  }

  mutation UserGroupSeatsPreviewInvoiceCreate($user: ID!, $product: ID!, $extraSeats: Int!) {
    groupSeatsPreviewInvoiceCreate(user: $user, product: $product, extraSeats: $extraSeats) {
      subtotal
      tax
      total
      items { id description quantity subtotal }
    }
  }

  mutation UserRequestEmailVerification($email: String!, $reason: String) {
    userRequestEmailVerification(email: $email, reason: $reason)
  }

  mutation UserConfirmEmailVerification($code: String!) {
    userConfirmEmailVerification(code: $code)
  }

  mutation UserUpdateBilling($id: ID!, $payment: String!) {
    userUpdateBilling(id: $id, payment: $payment) {
      ...UserSummaryFields
      billing { ...BillingInfoFields }
    }
  }

  mutation UserUpdate($id: ID!, $params: UserInput!) {
    userUpdate(id: $id, params: $params) {
      ...UserSummaryFields,
    }
  }

  mutation UserDelete($id: ID!) {
    userDelete(id: $id) { id }
  }

  mutation UserSubmitNPSSurvey($params: NPSSurveyInput!) {
    userSubmitNPSSurvey(params: $params) {
      id
      user
      course
      product
      event
      date
      nps
      comment
    }
  }

  fragment OrganizationTreeFields on Organization {
    parent {
      id
      name
    }
    children {
      id
      name
    }
  }

  fragment OrganizationSummaryFields on Organization {
    id
    name
    slug
    crqGradingStatus
  }

  fragment OrganizationAdminFields on Organization {
    createdAt
    updatedAt
    status
  }

  fragment ActionFields on Action {
    type
    name
    params
  }

  query OrganizationList($params: ListInput) {
    organizations(params: $params) {
      totalCount
      cursor
      items {
        ...OrganizationSummaryFields
        ...OrganizationAdminFields
        ...OrganizationTreeFields
      }
    }
  }

  query OrganizationGet($id: ID!) {
    organization(id: $id) {
      ...OrganizationSummaryFields
    }
  }

  query OrganizationGetAdmin($id: ID!) {
    organization(id: $id) {
      ...OrganizationSummaryFields
      ...OrganizationAdminFields
      ...OrganizationTreeFields
      memberships {
        totalCount
        items {
          ...MembershipSummaryFields
        }
      }
      actions { ...ActionFields }
    }
  }

  mutation OrganizationCreate($params: OrganizationInput!) {
    organizationCreate(params: $params) {
      ...OrganizationSummaryFields
      ...OrganizationAdminFields
      ...OrganizationTreeFields
      actions { ...ActionFields }
    }
  }

  mutation OrganizationUpdate($id: ID!, $params: OrganizationInput!) {
    organizationUpdate(id: $id, params: $params) {
      ...OrganizationSummaryFields
      ...OrganizationAdminFields
      ...OrganizationTreeFields
      actions { ...ActionFields }
    }
  }

  mutation OrganizationDelete($id: ID!) {
    organizationDelete(id: $id) { id }
  }

  fragment UserMembershipSummaryFields on Membership {
    id
    createdAt
    updatedAt
    org {
      ...OrganizationSummaryFields
      parent {
        id
      }
    }
    role
  }

  fragment MembershipSummaryFields on Membership {
    ...UserMembershipSummaryFields
    user { ...UserSummaryFields }
  }

  mutation MembershipCreate($params: MembershipInput!) {
    membershipCreate(params: $params) {
      ...MembershipSummaryFields
    }
  }

  mutation MembershipDelete($id: ID!) {
    membershipDelete(id: $id) { id }
  }

  fragment ProductFields on Product {
    id
    name
    description
    org {
      id
      name
    }
    slug
    scope {
      groups {
        ...ContentGroupFields
      }
    }
    consumables {
      type
      key
      quantity
    }
    price
    createdAt
    updatedAt
    live
    billing
    statementDescriptor
    autocancel
    interval
    intervalCount
    isOptional
    isPhysicalCard
    badge
    certificateImage
    stateLogoImage
    officialCourseDescription
    addons {
      id
      slug
      name
      description
      price
      isOptional
      isPhysicalCard
      badge
    }
    crossSells {
      id
      name
      description
      price
      badge
      interval
      intervalCount
      autocancel
      addons {
        id
        name
        description
        price
        isOptional
        badge
      }
    }
  }

  query ProductGet($id: ID!) {
    product(id: $id) {
      ...ProductFields
    }
  }

  query ProductGetPreview($id: ID!, $courses: [ID]) {
    product(id: $id) {
      ...ProductFields
      courses(ids: $courses) {
        id
        name
        displayName
        sections {
          id
          name
          concepts {
            id
            name
          }
        }
        assessments {
          cid
          type
          time
          questions
        }
      }
    }
  }

  query ProductList($params: ListInput) {
    products(params: $params) {
      totalCount
      cursor
      items {
        ...ProductFields
      }
    }
  }

  mutation ProductCreate($params: ProductInput!) {
    productCreate(params: $params) {
      ...ProductFields
    }
  }

  mutation ProductUpdate($id: ID!, $params: ProductInput!) {
    productUpdate(id: $id, params: $params) {
      ...ProductFields
    }
  }

  mutation ProductDelete($id: ID!) {
    productDelete(id: $id) { id }
  }

  fragment ProductKeyFields on ProductKey {
    id
    products {
      id
      name
    }
    courses { ...CourseDescriptionFields }
    createdAt
    claimedAt
    expiresAt
    claimedBy {
      id
      email
    }
    org {
      id
      name
    }
    duration
    status
    prefix
    multiUse
    payment
  }

  query ProductKeyGet($id: ID!) {
    productKey(id: $id) {
      ...ProductKeyFields
    }
  }

  query ProductKeyList($params: ListInput) {
    productKeys(params: $params) {
      totalCount
      cursor
      items {
        ...ProductKeyFields
      }
    }
  }

  mutation ProductKeyCreate($params: ProductKeyCreateInput!) {
    productKeyCreate(params: $params) {
      ...ProductKeyFields
    }
  }

  mutation ProductKeyUpdate($id: ID!, $params: ProductKeyUpdateInput!) {
    productKeyUpdate(id: $id, params: $params) {
      ...ProductKeyFields
    }
  }

  mutation ProductKeyDelete($id: ID!) {
    productKeyDelete(id: $id) { id }
  }

  query CourseGet($id: ID!) {
    course(id: $id) { ...CourseDescriptionFields }
  }

  fragment InviteFields on Invite {
    id
    createdAt
    updatedAt
    expiresAt
    claimedAt
    email
    sent
    status
    firstName
    lastName
    payment
    template
    unsubscribe
    billing
    billingOrg { ...OrganizationSummaryFields }
    duration
    user { ...UserSummaryFields }
    org { ...OrganizationSummaryFields }
    products { ...ProductFields }
    courses { ...CourseDescriptionFields }
    key { ...ProductKeyFields }
    role
    url
  }

  query InviteGet($id: ID!) {
    invite(id: $id) { ...InviteFields }
  }

  query InviteList($params: ListInput) {
    invites(params: $params) {
      totalCount
      cursor
      items {
        ...InviteFields
      }
    }
  }

  mutation InviteCreate($params: InviteInput!) {
    inviteCreate(params: $params) { ...InviteFields }
  }

  mutation InviteUpdate($id: ID!, $params: InviteInput!) {
    inviteUpdate(id: $id, params: $params) {
      ...InviteFields
    }
  }

  mutation InviteResend($id: ID!, $params: InviteInput) {
    inviteResend(id: $id, params: $params) {
      ...InviteFields
    }
  }

  mutation InviteAccept($token: String!, $payment: String, $confirm: Boolean) {
    inviteAccept(token: $token, payment: $payment, confirm: $confirm) {
      id
      email
      firstName
      lastName
      payment
      products { ...ProductFields }
      status
      user {
        ...UserSummaryFields
        memberships { ...UserMembershipSummaryFields }
        courses { ...CourseDescriptionFields }
      }
      org {
        id
        name
      }
    }
  }

  mutation InviteDelete($id: ID!) {
    inviteDelete(id: $id) { id }
  }
`;

function combineIdParams(...fields) {
  return (params) => {
    const id = fields.map((field) => params[field]).join('_');
    params = omit(params, fields);
    return { id, ...params };
  };
}

function updateParams(...inputParams) {
  return (entity) => {
    const id = entity.id;
    const params = {};

    inputParams.forEach((key) => {
      const isRef = key.startsWith('@');
      const path = isRef ? key.substring(1) : key;
      let val = get(entity, path);
      if (isRef) {
        val = isArray(val)
          ? val.map((v) => (isString(v) ? v : v && v.id))
          : isString(val)
            ? val
            : val && val.id;

        if (val === undefined) {
          return;
        }
      }
      set(params, path, val);
    });

    return { id, params };
  };
}

// Apollo swallows a throw from a mutation's `update` (see
// reportingCacheUpdate), so each of these enrollment updaters is wrapped: the
// mutation still reports success to its caller and the cache is still left
// half-updated, but the failure now reaches Sentry rather than only the
// browser console.
//
// None of them tell the student anything. The mutation did succeed on the
// server, and the symptom is a stale cache entry that the next query repairs,
// so a "something went wrong" message after a successful action would be
// misleading.
// We don't delete or reset enrollments very often and it is difficult to know
// what could be cached related to an enrollment, so just reset the whole cache.
// The promise is handed back rather than dropped so a reset that fails -- the
// cache cleared but never refetched -- is reported too.
const resetWholeStore = () => getApiClient().resetStore();

export const completeEnrollmentDelete = reportingCacheUpdate(
  'completeEnrollmentDelete',
  resetWholeStore,
);

export function cacheUserEnrollment(store, enrollment) {
  const userId = enrollment && enrollment.user && enrollment.user.id;
  if (!userId || !enrollment.user) {
    return;
  }

  const id = `User:${userId}`;
  const fragment = gql`
    fragment CreateEnrollmentFields on User {
      enrollments {
        id
      }
    }
  `;

  try {
    const oldData = store.readFragment({ fragment, id });
    const newData = {
      ...oldData,
      enrollments: oldData.enrollments
        .filter((item) => item.id !== enrollment.id)
        .concat(enrollment),
    };
    store.writeFragment({ fragment, id, data: newData });
  } catch (e) {
    console.error(e);
    // Ignore errors. It probably just means the data wasn't fetched before
  }
}

export const completeEnrollmentCreate = reportingCacheUpdate(
  'completeEnrollmentCreate',
  (store, { data }) => {
    const enrollment = values(data)[0];
    cacheUserEnrollment(store, enrollment);
  },
);

// A reset deletes the old enrollment and creates a new one, but the cache needs
// nothing beyond the same whole-store reset a delete does: resetStore() empties
// the cache and refetches every active query, so the new enrollment comes back
// from the server.
//
// Writing it back by hand as well looked like it helped and never did.
// resetStore() clears on a later microtask -- it is `Promise.resolve().then(()
// => clearStore())` -- so the write landed first and was wiped a tick later.
// Resequencing it after the reset would not help either: cacheUserEnrollment
// reads the existing list before writing, and against a just-cleared cache
// that read comes back empty and throws, which it swallows on purpose.
//
// Wrapped separately from completeEnrollmentDelete rather than aliased to it,
// so a failed reset still reports to Sentry under its own name -- a reset and
// a delete are different user actions and want telling apart in triage.
export const completeEnrollmentReset = reportingCacheUpdate(
  'completeEnrollmentReset',
  resetWholeStore,
);

export const api = {
  enrollments: {
    list: buildListQueryMethod(queries, 'EnrollmentList'),
    get: buildQueryMethod(queries, 'EnrollmentGet'),
    import: buildMutateMethod(queries, 'EnrollmentImport'),
    export: buildQueryMethod(queries, 'EnrollmentExport', null, null, {
      fetchPolicy: 'network-only',
    }),
    viewDashboard: buildMutateMethod(queries, 'EnrollmentViewDashboard'),
    viewContent: buildMutateMethod(queries, 'EnrollmentViewContent'),
    logActivity: buildMutateMethod(queries, 'EnrollmentLogActivity'),
    claim: buildMutateMethod(queries, 'EnrollmentClaim', null, null, {
      update: completeEnrollmentCreate,
    }),
    addPracticeTestAttempt: buildMutateMethod(
      queries,
      'EnrollmentAddPracticeTestAttempt',
    ),
    create: buildMutateMethod(queries, 'EnrollmentCreate', null, null, {
      update: completeEnrollmentCreate,
    }),
    delete: buildMutateMethod(queries, 'EnrollmentDelete', null, null, {
      update: completeEnrollmentDelete,
    }),
    reset: buildMutateMethod(queries, 'EnrollmentReset', null, null, {
      update: completeEnrollmentReset,
    }),
    recertify: buildMutateMethod(queries, 'EnrollmentRecertify'),
    startAssessment: buildMutateMethod(queries, 'EnrollmentStartAssessment'),
    resetAssessment: buildMutateMethod(queries, 'EnrollmentResetAssessment'),
    submitAssessmentFeedback: buildMutateMethod(
      queries,
      'EndrollmentAssessmentSubmitFeedback',
      null,
      (result) => result.question,
    ),
    submitAssessmentFeedbackSource: buildMutateMethod(
      queries,
      'EnrollmentAssessmentSubmitFeedbackSource',
      null,
      (result) => result.question,
    ),
    submitAssessmentResponse: buildMutateMethod(
      queries,
      'EnrollmentSubmitAssessmentResponse',
      null,
      (result) => result.question,
    ),
    submitAssessment: buildMutateMethod(
      queries,
      'EnrollmentSubmitAssessment',
      null,
      (result) => result.assessment,
    ),
    createGradingRequest: buildMutateMethod(queries, 'GradingRequestCreate'),
  },
  users: {
    list: buildListQueryMethod(queries, 'UserList'),
    listAWSUsers: buildListQueryMethod(queries, 'ListProviderUsers'),
    get: buildQueryMethod(queries, 'UserGet'),
    getBilling: buildQueryMethod(queries, 'UserGetBilling'),
    getInvoices: buildQueryMethod(queries, 'UserGetInvoices'),
    getSubscriptions: buildQueryMethod(queries, 'UserGetSubscriptions'),
    getCourses: buildQueryMethod(queries, 'UserGetCourses'),
    getEnrollments: buildQueryMethod(queries, 'UserGetEnrollments'),
    getEnrollmentTranslations: buildQueryMethod(
      queries,
      'UserGetEnrollmentTranslations',
    ),
    getMemberships: buildQueryMethod(queries, 'UserGetMemberships'),
    getJobSearchSaved: buildQueryMethod(queries, 'UserGetJobSearchSaved'),
    login: buildMutateMethod(queries, 'UserLogin'),
    update: buildMutateMethod(
      queries,
      'UserUpdate',
      updateParams(
        'lastName',
        'firstName',
        'email',
        'job',
        'disableActivityTimers',
        'preferredLanguage',
      ),
    ),
    updateBilling: buildMutateMethod(queries, 'UserUpdateBilling'),
    createPreviewSubscriptionInvoice: buildMutateMethod(
      queries,
      'CreatePreviewSubscriptionInvoice',
    ),
    createPreviewSubscriptionInvoiceWithSeats: buildMutateMethod(
      queries,
      'CreatePreviewSubscriptionInvoiceWithSeats',
    ),
    cancelSubscription: buildMutateMethod(queries, 'UserCancelSubscription'),
    removeSubscription: buildMutateMethod(queries, 'UserRemoveSubscription'),
    createSubscription: buildMutateMethod(queries, 'UserCreateSubscription'),
    createSubscriptionBundle: buildMutateMethod(
      queries,
      'UserCreateSubscriptionBundle',
    ),
    createSubscriptionBundleWithSeats: buildMutateMethod(
      queries,
      'UserCreateSubscriptionBundleWithSeats',
    ),
    groupSeatsPurchase: buildMutateMethod(queries, 'UserGroupSeatsPurchase'),
    groupSeatsPreviewInvoice: buildMutateMethod(
      queries,
      'UserGroupSeatsPreviewInvoiceCreate',
    ),
    addonPurchase: buildMutateMethod(queries, 'UserAddonPurchase'),
    addonPurchasePreviewInvoice: buildMutateMethod(
      queries,
      'UserAddonPurchasePreviewInvoiceCreate',
    ),
    requestEmailVerification: buildMutateMethod(
      queries,
      'UserRequestEmailVerification',
    ),
    confirmEmailVerification: buildMutateMethod(
      queries,
      'UserConfirmEmailVerification',
    ),
    delete: buildMutateMethod(queries, 'UserDelete'),
    submitNPSSurvey: buildMutateMethod(queries, 'UserSubmitNPSSurvey'),
  },
  organizations: {
    list: buildListQueryMethod(queries, 'OrganizationList'),
    get: buildQueryMethod(queries, 'OrganizationGet'),
    getAdmin: buildQueryMethod(
      queries,
      'OrganizationGetAdmin',
      null,
      (org) =>
        org && {
          ...org,
          memberships: (org && org.memberships.items) || [],
        },
    ),
    create: buildMutateMethod(
      queries,
      'OrganizationCreate',
      updateParams(
        'name',
        'slug',
        'status',
        'actions',
        'crqGradingStatus',
        '@parent',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'OrganizationUpdate',
      updateParams(
        'name',
        'slug',
        'status',
        'actions',
        'crqGradingStatus',
        '@parent',
      ),
    ),
    delete: buildMutateMethod(queries, 'OrganizationDelete'),
  },
  products: {
    get: buildQueryMethod(queries, 'ProductGet'),
    getPreview: buildQueryMethod(queries, 'ProductGetPreview'),
    list: buildListQueryMethod(queries, 'ProductList'),
    create: buildMutateMethod(
      queries,
      'ProductCreate',
      updateParams(
        'name',
        'description',
        '@org',
        'slug',
        '@scope.groups',
        'price',
        'live',
        'billing',
        'statementDescriptor',
        'interval',
        'intervalCount',
        'autocancel',
        'consumables',
        '@addons',
        '@crossSells',
        'isOptional',
        'isPhysicalCard',
        'badge',
        'certificateImage',
        'stateLogoImage',
        'officialCourseDescription',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'ProductUpdate',
      updateParams(
        'name',
        'description',
        '@org',
        'slug',
        '@scope.groups',
        'price',
        'live',
        'billing',
        'statementDescriptor',
        'interval',
        'intervalCount',
        'autocancel',
        'consumables',
        '@addons',
        '@crossSells',
        'isOptional',
        'isPhysicalCard',
        'badge',
        'certificateImage',
        'stateLogoImage',
        'officialCourseDescription',
      ),
    ),
  },
  productKeys: {
    get: buildQueryMethod(queries, 'ProductKeyGet'),
    list: buildListQueryMethod(queries, 'ProductKeyList'),
    create: buildMutateMethod(
      queries,
      'ProductKeyCreate',
      updateParams(
        'prefix',
        'count',
        '@products',
        '@courses',
        '@org',
        'expiresAt',
        'duration',
        'expiresAt',
        'status',
        'multiUse',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'ProductKeyUpdate',
      updateParams('status'),
    ),
    delete: buildMutateMethod(queries, 'ProductKeyDelete'),
  },
  content: {
    groups: {
      list: buildListQueryMethod(queries, 'ContentGroupList'),
    },
    courses: {
      get: buildQueryMethod(queries, 'CourseGet'),
    },
  },
  memberships: {
    create: buildMutateMethod(
      queries,
      'MembershipCreate',
      updateParams('@org', '@user', 'role'),
    ),
    delete: buildMutateMethod(
      queries,
      'MembershipDelete',
      combineIdParams('org', 'user'),
    ),
  },
  invites: {
    list: buildListQueryMethod(queries, 'InviteList'),
    get: buildQueryMethod(queries, 'InviteGet'),
    create: buildMutateMethod(
      queries,
      'InviteCreate',
      updateParams(
        'email',
        '@org',
        '@billingOrg',
        '@products',
        '@courses',
        'duration',
        'expiresAt',
        'prefix',
        'send',
        'payment',
        'template',
        'unsubscribe',
        'firstName',
        'lastName',
        'role',
      ),
    ),
    update: buildMutateMethod(
      queries,
      'InviteUpdate',
      updateParams(
        'email',
        'send',
        'payment',
        'template',
        'unsubscribe',
        'firstName',
        'lastName',
        'role',
      ),
    ),
    accept: buildMutateMethod(queries, 'InviteAccept'),
    delete: buildMutateMethod(queries, 'InviteDelete'),
    resend: buildMutateMethod(queries, 'InviteResend'),
  },
};

api.Product = api.products;
api.User = api.users;
api.Enrollment = api.enrollments;
api.Organization = api.organizations;
api.ProductKey = api.productKeys;
api.Membership = api.memberships;
api.Invite = api.invites;

export function isPassingQuizScore(score) {
  let { normalized, raw, max } = score || {};

  if (!isNumber(normalized) && max > 0) {
    normalized = raw / max;
  }

  return normalized && Math.round(normalized * 100) >= 70;
}
